import { type Writable, writable } from 'svelte/store';
import type { ModelConfig } from '$lib/apis';
import type { Socket } from 'socket.io-client';

// Backend
export const config: Writable<Config | undefined> = writable(undefined);
export const user: Writable<SessionUser | undefined> = writable(undefined);

// Frontend
export const MODEL_DOWNLOAD_POOL = writable({});

export const mobile = writable(false);

export const socket: Writable<null | Socket> = writable(null);
export const activeUserIds: Writable<null | string[]> = writable(null);
export const USAGE_POOL: Writable<null | string[]> = writable(null);

export const theme = writable('light');

export const TTSWorker = writable(null);

export const chatId = writable('');
export const chatTitle = writable('');

export const chats = writable(null);
export const pinnedChats = writable([]);

export const models: Writable<Model[]> = writable([]);

export const knowledge: Writable<null | Document[]> = writable(null);

export const settings: Writable<Settings> = writable({});

export const showSidebar = writable(false);
export const showSettings = writable(false);

export const showControls = writable(false);
export const showOverview = writable(false);
export const showArtifacts = writable(false);

export const temporaryChatEnabled = writable(false);
export const scrollPaginationEnabled = writable(false);
export const currentChatPage = writable(1);

export const isLastActiveTab = writable(true);
export const playingNotificationSound = writable(false);

export type Model = OpenAIModel;

type BaseModel = {
	id: string;
	name: string;
	info?: ModelConfig;
	owned_by: 'ollama' | 'openai' | 'arena';
};

export interface OpenAIModel extends BaseModel {
	owned_by: 'openai';
	external: boolean;
	source?: string;
}

export type Settings = {
	surveyVisited?: string;
};

type Document = {
	collection_name: string;
	filename: string;
	name: string;
	title: string;
};

export type BrandUrls = {
	shop_redirect: string | null;
	shop_upgrade: string | null;
	imprint: string | null;
	privacy: string | null;
	terms: string | null;
	privacy_manifest: string | null;
	help: string | null;
	control_panel: string | null;
	application: string | null;
	sunset_info: string | null;
	logout_endpoint: string | null;
	survey: string | null;
};

export type Config = {
	status: boolean;
	name: string;
	company_name: string;
	pwa_enabled: boolean;
	version: string;
	default_locale: string;
	market: string | null;
	brand: string | null;
	reasoning_effort_default: 'low' | 'medium' | 'high';
	sunset_start_date?: string | null;
	sunset_due_date?: string | null;
	feature_announcement_start_date?: string | null;
	feature_announcement_end_date?: string | null;
	default_model: string;
	default_prompt_suggestions: PromptSuggestion[];
	urls: BrandUrls;
	features: {
		auth: boolean;
		enable_api_key: boolean;
		enable_web_search?: boolean;
		enable_image_generation: boolean;
		enable_admin_export: boolean;
		enable_admin_chat_access: boolean;
		enable_community_sharing: boolean;
		enable_autocomplete_generation: boolean;
		ionos_show_early_adopter_invitation: boolean;
		ionos_registration_url: string|null;
		ionos_password_reset_url: string|null;
		ionos_survey_new_users_url: string|null;
	};
	oauth: {
		providers: {
			[key: string]: string;
		};
	};
};

type PromptSuggestion = {
	content: string;
	title: [string, string];
};

export enum AccountType {
	UNKNOWN = 'unknown',
	TBB = 'TBB',
	PAID = 'paid'
}

export type SessionUser = {
	id: string;
	role: string;
	created_at: number;
	pseudonymized_user_id: string|null;
	account_type: AccountType;
	market: string;
};
